import { useState } from "react";
import { Share2, Check, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareButton
 * - Builds a public link to /api/share/{id} (server-rendered Open Graph card)
 * - Tries Web Share API on mobile, falls back to clipboard copy on desktop
 */
export const ShareButton = ({ itineraryId, title, subtitle, className = "", testId = "share-itinerary-btn", variant = "primary" }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `${BACKEND_URL}/api/share/${itineraryId}`;

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: title || "VYBE Trip",
            text: subtitle || "Check out this AI-curated viral itinerary.",
            url,
          });
          toast.success("Shared");
        } catch (err) {
          if (err?.name !== "AbortError") {
            await copyToClipboard();
          }
        }
      } else {
        await copyToClipboard();
      }
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // Fallback: temporary input
      const i = document.createElement("input");
      i.value = url;
      document.body.appendChild(i);
      i.select();
      document.execCommand("copy");
      document.body.removeChild(i);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const Icon = busy ? Loader2 : copied ? Check : navigator.share ? Share2 : Copy;
  const label = busy ? "Sharing…" : copied ? "Copied" : "Share";

  const base = "font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-5 py-3 inline-flex items-center gap-2 transition active:scale-[0.98]";
  const styles = variant === "primary"
    ? "gradient-pink-cyan text-black hover:opacity-95"
    : "glass text-white hover:bg-white/10";

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testId}
      className={`${base} ${styles} ${className} disabled:opacity-70`}
      disabled={busy}
      title={url}
    >
      <Icon className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
      <span>{label}</span>
    </button>
  );
};
